import { useState, useRef } from 'react'
import { useAppSelector, useAppDispatch } from '@state/hooks'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { parseImageMarkers } from '@utils/imageMarkerParser'
import { buildAnalysisReportMarkdown } from '@utils/analysisReportExporter'
import CollapsibleQRSection from '@components/shared/CollapsibleQRSection'
import { useTabNavigation } from '@contexts/TabNavigationContext'
import { navigateToMeceTable, navigateToStrategyMap, navigateToGraphNode, parseNavigationLink } from '@utils/crossTabNavigation'
import { setActiveDocument } from '@state/slices/documentSlice'
import type { DocType } from '@state/slices/documentSlice'
import { MetricBadgeList } from '@components/shared/MetricBadge'
import { useScrollBookmark } from '@utils/useScrollBookmark'
import { MeasuresInitiativesRegister } from '@components/report/MeasuresInitiativesRegister'
import { PillarKeyResults } from '@components/report/PillarKeyResults'
import { StaleNarrativeBanner } from '@components/report/StaleNarrativeBanner'
import { regenerateAnalysisReport, refreshNarratives } from '@state/slices/conversationsSlice'
import type { NarrativeStatusByField } from '../../types/narrativeRefresh'
export type { NarrativeStatusByField } from '../../types/narrativeRefresh'
import './AnalysisReportTab.css'

// Phase 2-F: map narrativeRefreshStatus from the regenerate response to the
// success-state button label. Centralized so the rendering branch stays a
// single source of truth and is unit-testable.
// Phase 4: optionally accept narrativeStatusByField to render a specific
// missing-narratives list on partial runs.

const FIELD_LABELS: Record<keyof NarrativeStatusByField, string> = {
  pillar_narratives:     'pillar narratives',
  relationship_analysis: 'relationship analysis',
  why_it_matters:        'why-it-matters',
  bsc_narratives:        'BSC perspective narratives',
}

const FIELD_ORDER: readonly (keyof NarrativeStatusByField)[] = [
  'pillar_narratives',
  'relationship_analysis',
  'why_it_matters',
  'bsc_narratives',
]

export function regenDoneLabel(
  narrativeRefreshStatus?: 'refreshed' | 'partial' | 'skipped',
  narrativeStatusByField?: NarrativeStatusByField,
): string {
  if (narrativeRefreshStatus === 'partial' && narrativeStatusByField) {
    const refreshed = FIELD_ORDER.filter(f => narrativeStatusByField[f] === 'refreshed')
    const skipped   = FIELD_ORDER.filter(f => narrativeStatusByField[f] === 'skipped')
    const missingLabel = skipped.map(f => FIELD_LABELS[f]).join(', ')
    return (
      `Plan regenerated; ${refreshed.length} of ${FIELD_ORDER.length} narratives refreshed ` +
      `(${missingLabel} did not refresh)`
    )
  }
  if (narrativeRefreshStatus === 'partial') {
    return 'Plan regenerated; some narratives could not be refreshed'
  }
  if (narrativeRefreshStatus === 'skipped') {
    return 'Plan regenerated; narrative refresh skipped'
  }
  // 'refreshed' OR undefined (pre-2-F backend backward-compat path)
  return 'Plan Regenerated!'
}

/**
 * Phase 5: refresh-narratives done-label, mirrors regenDoneLabel but with
 * Phase-5-specific copy. Reuses FIELD_LABELS + FIELD_ORDER constants above.
 */
export function refreshDoneLabel(
  status?: 'refreshed' | 'partial' | 'skipped',
  perField?: NarrativeStatusByField,
): string {
  if (status === 'skipped') {
    return 'No narratives to refresh'
  }
  if (status === 'partial' && perField) {
    const skippedFields = FIELD_ORDER.filter(f => perField[f] === 'skipped')
    if (skippedFields.length === 0) {
      return 'Some narratives could not be refreshed'
    }
    const skippedLabels = skippedFields.map(f => FIELD_LABELS[f]).join(', ')
    const refreshedCount = FIELD_ORDER.length - skippedFields.length
    return `${refreshedCount} of ${FIELD_ORDER.length} narratives refreshed (${skippedLabels} did not refresh)`
  }
  if (status === 'partial') {
    return 'Some narratives could not be refreshed'
  }
  return 'Narratives refreshed!'
}

/**
 * Extract image URLs from markdown text
 * Matches: ![alt](url)
 */
function extractImageUrls(text: string): string[] {
  const imageRegex = /!\[.*?\]\((.*?)\)/g
  const urls: string[] = []
  let match

  while ((match = imageRegex.exec(text)) !== null) {
    urls.push(match[1])
  }

  return urls
}

/**
 * Extract filename from URL or file path
 * Examples:
 *   "http://127.0.0.1:8000/static/assets/Metrics/metric_cash_flow.jpg" -> "metric_cash_flow.jpg"
 *   "/static/visuals/Monthly_Revenue_20260121.png" -> "Monthly_Revenue_20260121.png"
 */
function getFilename(url: string): string {
  return url.split('/').pop() || url
}

/**
 * Replace duplicate images with reference links
 * @param text - Original markdown text
 * @param displayedImages - Map of filename -> first response index where it appeared
 * @param currentIndex - Current response index
 * @returns Modified markdown text with references instead of duplicate images
 */
function deduplicateImages(
  text: string,
  displayedImages: Map<string, number>,
  currentIndex: number
): string {
  let processedText = text
  const imageUrls = extractImageUrls(text)

  imageUrls.forEach(url => {
    const filename = getFilename(url)
    const firstSeenIndex = displayedImages.get(filename)

    // If this image was displayed in a previous response
    if (firstSeenIndex !== undefined && firstSeenIndex < currentIndex) {
      // Extract image alt text from the markdown
      const imageRegex = new RegExp(`!\\[(.*?)\\]\\(${url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\)`, 'g')
      const match = imageRegex.exec(text)
      const altText = match ? match[1] : filename.replace(/\.(jpg|png|jpeg)$/i, '')

      // Replace with reference link
      const referenceLink = `\n\n_↑ See **${altText}** in [Response ${firstSeenIndex + 1}](#quick-response-${firstSeenIndex})_\n\n`
      processedText = processedText.replace(imageRegex, referenceLink)
    } else if (firstSeenIndex === undefined) {
      // First time seeing this image - track it
      displayedImages.set(filename, currentIndex)
    }
  })

  return processedText
}

function AnalysisReportTab() {
  const dispatch = useAppDispatch()
  const { navigateToTab } = useTabNavigation()
  const { activeConversationId, conversations } = useAppSelector(state => state.conversations)
  const activeConversation = conversations.find(c => c.id === activeConversationId)

  const reportData = activeConversation?.analysisReportData
  const strategyMap = activeConversation?.strategyWorkspaceData?.strategyMap

  // Collapsible section states
  const [isFullReportExpanded, setIsFullReportExpanded] = useState(true)
  const [isObjectivesExpanded, setIsObjectivesExpanded] = useState(true)
  const [isMeceExpanded, setIsMeceExpanded] = useState(true)
  const [isDeepModeExpanded, setIsDeepModeExpanded] = useState(true)
  const [isQuickInsightsExpanded, setIsQuickInsightsExpanded] = useState(true)
  const [isAppendixExpanded, setIsAppendixExpanded] = useState(false)  // Appendix collapsed by default
  const [isCoverageExpanded, setIsCoverageExpanded] = useState(true)
  const [isSynthesisExpanded, setIsSynthesisExpanded] = useState(true)

  // Export functionality
  const reportContentRef = useRef<HTMLDivElement>(null)
  // Scroll container is the outer .analysis-report div (overflow-y: auto),
  // not .report-content (overflow: visible) — used for scroll bookmarks.
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [exportStatus, setExportStatus] = useState<'idle' | 'copying' | 'copied' | 'copy-failed' | 'pdf-generating' | 'pdf-done' | 'pdf-failed'>('idle')
  const [regenStatus, setRegenStatus] = useState<'idle' | 'regenerating' | 'regen-done' | 'regen-failed'>('idle')
  const [regenSuccessLabel, setRegenSuccessLabel] = useState<string>('Plan Regenerated!')
  const [refreshStatus, setRefreshStatus] = useState<'idle' | 'refreshing' | 'refresh-done' | 'refresh-failed'>('idle')

  useScrollBookmark({
    containerRef: scrollContainerRef,
    conversationId: activeConversationId,
    tabId: 'analysis-report',
    ready: Boolean(reportData),
    firstVisit: 'bottom',
  })

  // Phase 4: Navigation handlers
  const handleNavigateToMece = (pillarName?: string) => {
    navigateToMeceTable(navigateToTab, undefined, pillarName)
  }

  const handleNavigateToStrategyMap = () => {
    navigateToStrategyMap(navigateToTab)
  }

  const handleNavigateToNode = (nodeId: string) => {
    navigateToGraphNode(nodeId, navigateToTab, dispatch)
  }

  const handleDocumentLink = (href: string) => {
    const navTarget = parseNavigationLink(href)
    if (navTarget && navTarget.tab === 'document' && navTarget.sectionId) {
      const [docType, ...nodeIdParts] = navTarget.sectionId.split(':')
      const nodeId = nodeIdParts.join(':')
      if (docType && nodeId) {
        dispatch(setActiveDocument({ nodeId, docType: docType as DocType }))
        navigateToTab('document')
      }
    }
  }

  // Markdown components override for intercepting document links AND mounting live components
  const markdownComponents = {
    a: ({ href, children, ...props }: any) => {
      if (href && href.startsWith('#document:')) {
        return (
          <a
            href={href}
            onClick={(e) => {
              e.preventDefault()
              handleDocumentLink(href)
            }}
            className="document-link"
            {...props}
          >
            {children}
          </a>
        )
      }
      return <a href={href} {...props}>{children}</a>
    },
    code: ({ inline, className, children, ...props }: any) => {
      const langMatch = /language-(\S+)/.exec(className || '')
      const lang = langMatch?.[1]
      if (lang === 'pillar-key-results' && !inline) {
        const pillarId = (Array.isArray(children) ? children.join('') : String(children ?? '')).trim()
        return <PillarKeyResults strategyMap={strategyMap} pillarId={pillarId} />
      }
      return <code className={className} {...props}>{children}</code>
    },
  }

  const handleRegenerateStrategyPlan = async () => {
    if (!activeConversationId || regenStatus === 'regenerating') return
    console.info('[AnalysisReport] Regenerate clicked')
    setRegenStatus('regenerating')
    try {
      const payload = await dispatch(regenerateAnalysisReport({ conversationId: activeConversationId })).unwrap()
      setRegenSuccessLabel(regenDoneLabel(payload?.narrativeRefreshStatus, payload?.narrativeStatusByField))
      setRegenStatus('regen-done')
      setTimeout(() => setRegenStatus('idle'), 2000)
    } catch (err) {
      console.error('[AnalysisReport] Regenerate failed:', err)
      setRegenStatus('regen-failed')
      setTimeout(() => setRegenStatus('idle'), 3000)
    }
  }

  const handleRefreshNarratives = async () => {
    if (!activeConversationId || refreshStatus === 'refreshing') return
    console.info('[AnalysisReport] Refresh Narratives clicked')
    setRefreshStatus('refreshing')
    try {
      // Phase 5: thunk's updateAnalysisReport dispatch flips narrativeStaleness.isStale
      // to false, which hides the banner automatically. No success-label state needed
      // until a future phase adds a standalone Refresh CTA button (D9 carry-forward).
      await dispatch(refreshNarratives({ conversationId: activeConversationId })).unwrap()
      setRefreshStatus('refresh-done')
      setTimeout(() => setRefreshStatus('idle'), 2000)
    } catch (err) {
      console.error('[AnalysisReport] Refresh Narratives failed:', err)
      setRefreshStatus('refresh-failed')
      setTimeout(() => setRefreshStatus('idle'), 3000)
    }
  }

  // Export: Copy to Clipboard
  const handleCopyToClipboard = async () => {
    if (!reportData) return
    setExportStatus('copying')
    try {
      const markdown = buildAnalysisReportMarkdown(reportData, strategyMap)
      await navigator.clipboard.writeText(markdown)
      setExportStatus('copied')
      setTimeout(() => setExportStatus('idle'), 2000)
    } catch (err) {
      console.error('[AnalysisReport] Copy to clipboard failed:', err)
      setExportStatus('copy-failed')
      setTimeout(() => setExportStatus('idle'), 3000)
    }
  }

  // Export: PDF
  const handleExportPdf = async () => {
    if (!reportContentRef.current) return
    setExportStatus('pdf-generating')

    // Save current expansion states
    const prevStates = {
      fullReport: isFullReportExpanded,
      objectives: isObjectivesExpanded,
      mece: isMeceExpanded,
      deepMode: isDeepModeExpanded,
      quickInsights: isQuickInsightsExpanded,
      appendix: isAppendixExpanded,
      coverage: isCoverageExpanded,
      synthesis: isSynthesisExpanded,
    }

    // Force all expanded so PDF captures everything
    setIsFullReportExpanded(true)
    setIsObjectivesExpanded(true)
    setIsMeceExpanded(true)
    setIsDeepModeExpanded(true)
    setIsQuickInsightsExpanded(true)
    setIsAppendixExpanded(true)
    setIsCoverageExpanded(true)
    setIsSynthesisExpanded(true)

    // Wait for React to re-render expanded sections
    await new Promise(resolve => setTimeout(resolve, 100))

    try {
      const html2pdf = (await import('html2pdf.js')).default

      // Hide export buttons during capture
      reportContentRef.current.classList.add('pdf-export-mode')

      await html2pdf()
        .set({
          margin: [10, 10, 10, 10],
          filename: `omap-analysis-report-${new Date().toISOString().split('T')[0]}.pdf`,
          image: { type: 'jpeg', quality: 0.95 },
          html2canvas: { scale: 2, useCORS: true, logging: false, windowWidth: 800 },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
        })
        .from(reportContentRef.current)
        .save()

      reportContentRef.current.classList.remove('pdf-export-mode')
      setExportStatus('pdf-done')
      setTimeout(() => setExportStatus('idle'), 2000)
    } catch (err) {
      console.error('[AnalysisReport] PDF export failed:', err)
      reportContentRef.current?.classList.remove('pdf-export-mode')
      setExportStatus('pdf-failed')
      setTimeout(() => setExportStatus('idle'), 3000)
    } finally {
      // Restore previous expansion states
      setIsFullReportExpanded(prevStates.fullReport)
      setIsObjectivesExpanded(prevStates.objectives)
      setIsMeceExpanded(prevStates.mece)
      setIsDeepModeExpanded(prevStates.deepMode)
      setIsQuickInsightsExpanded(prevStates.quickInsights)
      setIsAppendixExpanded(prevStates.appendix)
      setIsCoverageExpanded(prevStates.coverage)
      setIsSynthesisExpanded(prevStates.synthesis)
    }
  }

  if (!activeConversationId) {
    return (
      <div className="analysis-report-empty">
        <div className="empty-message">
          <h2>Analysis Report</h2>
          <p>Detailed strategic analysis will appear here:</p>
          <ul>
            <li>📋 Coverage Attestation</li>
            <li>📊 MECE Analysis Summaries</li>
            <li>📈 Strategic Recommendations</li>
            <li>🔗 Evidence Grounding & Citations</li>
          </ul>
          <p className="hint">Start a conversation and use Deep or Comprehensive mode</p>
        </div>
      </div>
    )
  }

  if (!reportData || (!reportData.coverageAttestation && !reportData.strategicSynthesis && !reportData.meceAnalysis && !reportData.fullReport && !reportData.quickResponses)) {
    return (
      <div className="analysis-report-empty">
        <div className="empty-message">
          <h2>Analysis Report</h2>
          <p>No analysis report yet for this conversation</p>
          <p className="hint">Ask a strategic question using Deep or Comprehensive mode</p>
        </div>
      </div>
    )
  }

  // Determine section ordering: most recent results appear first
  const quickInsightsFirst = (() => {
    if (!reportData.quickResponses?.length || !reportData.fullReport) return false
    const latestQuickTs = reportData.quickResponses[reportData.quickResponses.length - 1]?.timestamp
    const strategyTs = reportData.strategyPlanGeneratedAt
    if (!latestQuickTs || !strategyTs) return false
    return new Date(latestQuickTs) > new Date(strategyTs)
  })()

  // Pre-render Quick Insights section for dynamic positioning
  const quickInsightsSection = reportData.quickResponses && reportData.quickResponses.length > 0 ? (() => {
    const displayedImages = new Map<string, number>()
    const processedResponses = reportData.quickResponses!.map((response: any, index: number) => {
      const parsedAnswer = parseImageMarkers(response.answer)
      const deduplicatedAnswer = deduplicateImages(parsedAnswer, displayedImages, index)
      return { ...response, processedAnswer: deduplicatedAnswer }
    })
    return (
      <section
        id="ar-section-quick-insights"
        data-bookmark-anchor
        className={`report-section collapsible-section ${isQuickInsightsExpanded ? 'expanded' : 'collapsed'}`}
      >
        <div
          className="section-header clickable"
          onClick={() => setIsQuickInsightsExpanded(!isQuickInsightsExpanded)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              setIsQuickInsightsExpanded(!isQuickInsightsExpanded)
            }
          }}
          aria-expanded={isQuickInsightsExpanded}
        >
          <span className="collapse-icon">{isQuickInsightsExpanded ? '\u25BC' : '\u25B6'}</span>
          <h2>Quick Insights</h2>
          {displayedImages.size > 0 && (
            <span className="info-badge" title="Duplicate images replaced with references">
              {displayedImages.size} unique image{displayedImages.size !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        {isQuickInsightsExpanded && (
          <div className="section-content">
            {processedResponses.map((response: any, index: number) => (
              <div key={index} id={`ar-qr-${index}`} data-bookmark-anchor>
                <CollapsibleQRSection
                  question={response.question}
                  answer={response.processedAnswer}
                  index={index}
                  defaultExpanded={true}
                />
              </div>
            ))}
          </div>
        )}
      </section>
    )
  })() : null

  return (
    <div className="analysis-report" ref={scrollContainerRef}>
      <div className="report-content" ref={reportContentRef}>

        {/* 1. Company Context Banner */}
        {reportData.companyIntelligence?.company_name && (
          <div className="company-context-banner">
            <span className="company-badge">{reportData.companyIntelligence.company_name}</span>
            {reportData.companyIntelligence.industry_description && (
              <span className="industry-badge">{reportData.companyIntelligence.industry_description}</span>
            )}
            {reportData.companyIntelligence.revenue_tier && (
              <span className="revenue-badge">{reportData.companyIntelligence.revenue_tier}</span>
            )}
          </div>
        )}

        {/* Quick Insights rendered FIRST when more recent than Strategy Plan */}
        {quickInsightsFirst && quickInsightsSection}

        {/* Phase 5: banner shows when narratives are stale (inputs changed)
            OR legacy sentinel-missing path. Single CTA: Refresh Narratives. */}
        {(() => {
          const staleness = reportData.narrativeStaleness
          const legacy = reportData.fullReport && !reportData.fullReport.includes('pillar-key-results')
          if (staleness?.isStale) {
            return (
              <StaleNarrativeBanner
                mode="inputs-changed"
                inputsChangedAt={staleness.inputsChangedAt ?? undefined}
                onRefreshClick={handleRefreshNarratives}
              />
            )
          }
          if (legacy) {
            return (
              <StaleNarrativeBanner
                mode="legacy"
                onRefreshClick={handleRefreshNarratives}
              />
            )
          }
          return null
        })()}

        {/* 2. Strategic Analysis Report (fullReport) */}
        {reportData.fullReport && (
          <section
            id="ar-section-full-report"
            data-bookmark-anchor
            className={`report-section collapsible-section ${isFullReportExpanded ? 'expanded' : 'collapsed'}`}
          >
            <div
              className="section-header clickable"
              onClick={() => setIsFullReportExpanded(!isFullReportExpanded)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setIsFullReportExpanded(!isFullReportExpanded)
                }
              }}
              aria-expanded={isFullReportExpanded}
            >
              <span className="collapse-icon">{isFullReportExpanded ? '\u25BC' : '\u25B6'}</span>
              <h2>Strategic Analysis Report</h2>
              {reportData.planExecuted && (
                <span className="report-badge">Executed Analysis</span>
              )}
            </div>
            {isFullReportExpanded && (
              <div className="section-content">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                  {parseImageMarkers(reportData.fullReport)}
                </ReactMarkdown>
              </div>
            )}
          </section>
        )}

        {/* 3. BSC Strategic Objectives */}
        {reportData.strategicObjectives && reportData.strategicObjectives.length > 0 && (
          <section
            id="ar-section-objectives"
            data-bookmark-anchor
            className={`report-section objectives-section collapsible-section ${isObjectivesExpanded ? 'expanded' : 'collapsed'}`}
          >
            <div
              className="section-header clickable"
              onClick={() => setIsObjectivesExpanded(!isObjectivesExpanded)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setIsObjectivesExpanded(!isObjectivesExpanded)
                }
              }}
              aria-expanded={isObjectivesExpanded}
            >
              <span className="collapse-icon">{isObjectivesExpanded ? '\u25BC' : '\u25B6'}</span>
              <h2>BSC Strategic Objectives</h2>
            </div>
            {isObjectivesExpanded && (
              <div className="section-content">
                <p className="section-intro">
                  These strategic objectives define the <strong>"why"</strong> behind the tactical initiatives.
                  Each objective is linked to specific initiatives in the Strategy Map.
                </p>

                <div className="objectives-by-perspective">
                  {['Financial', 'Customer', 'Internal Process', 'Learning & Growth'].map(perspective => {
                    const objectives = reportData.strategicObjectives?.filter(
                      (o: any) => o.perspective === perspective
                    ) || []
                    if (objectives.length === 0) return null

                    return (
                      <div key={perspective} className="perspective-objectives">
                        <h3 className="perspective-heading">{perspective}</h3>
                        {objectives.map((obj: any) => (
                          <div key={obj.objective_id} className="objective-card">
                            <div className="objective-card-header">
                              <span className="obj-id-badge">{obj.objective_id}</span>
                              <span className="obj-intent">{obj.strategic_intent}</span>
                              <span className={`outcome-badge ${obj.outcome_type?.toLowerCase()}`}>
                                {obj.outcome_type}
                              </span>
                            </div>
                            {obj.description && (
                              <p className="obj-description">{obj.description}</p>
                            )}
                            {obj.initiative_count > 0 && (
                              <div className="supporting-initiatives">
                                <span className="initiatives-label">Supporting Initiatives:</span>
                                <span className="initiatives-count">{obj.initiative_count}</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )
                  })}
                </div>

                {/* Quick Navigation to Strategy Map */}
                <div className="objectives-navigation">
                  <span className="nav-label">See objectives in action:</span>
                  <button
                    className="btn-nav-link"
                    onClick={handleNavigateToStrategyMap}
                    title="View Strategy Map with objectives and initiatives"
                  >
                    <span className="nav-icon">&#128506;</span>
                    Strategy Map
                  </button>
                </div>
              </div>
            )}
          </section>
        )}

        {/* 3b. Measures & Initiatives Register (K&N Phase 1) */}
        <MeasuresInitiativesRegister strategyMap={strategyMap} />

        {/* 4. MECE Analysis - Collapsible */}
        {reportData.meceAnalysis && (
          <section
            id="ar-section-mece"
            data-bookmark-anchor
            className={`report-section collapsible-section ${isMeceExpanded ? 'expanded' : 'collapsed'}`}
          >
            <div
              className="section-header clickable"
              onClick={() => setIsMeceExpanded(!isMeceExpanded)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setIsMeceExpanded(!isMeceExpanded)
                }
              }}
              aria-expanded={isMeceExpanded}
            >
              <span className="collapse-icon">{isMeceExpanded ? '\u25BC' : '\u25B6'}</span>
              <h2>MECE Scenario Analysis</h2>
            </div>
            {isMeceExpanded && (
              <div className="section-content">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {parseImageMarkers(reportData.meceAnalysis)}
                </ReactMarkdown>
              </div>
            )}
          </section>
        )}

        {/* 5. Deep Mode Investigation Plan Summary - Collapsible */}
        {reportData.deepModePlan && reportData.planGenerated && (
          <section
            id="ar-section-deep-mode"
            data-bookmark-anchor
            className={`report-section collapsible-section ${isDeepModeExpanded ? 'expanded' : 'collapsed'}`}
          >
            <div
              className="section-header clickable"
              onClick={() => setIsDeepModeExpanded(!isDeepModeExpanded)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setIsDeepModeExpanded(!isDeepModeExpanded)
                }
              }}
              aria-expanded={isDeepModeExpanded}
            >
              <span className="collapse-icon">{isDeepModeExpanded ? '\u25BC' : '\u25B6'}</span>
              <h2>Deep Mode Investigation Plan</h2>
            </div>
            {isDeepModeExpanded && (
              <div className="section-content">
                <div className="plan-metadata">
                  <p><strong>Query:</strong> {reportData.deepModePlan.user_query}</p>
                  <p><strong>Anchor Node:</strong> {reportData.deepModePlan.anchor_node}</p>
                  <p><strong>Hypotheses:</strong> {reportData.deepModePlan.hypotheses?.length || 0}</p>
                  {reportData.deepModeReport?.metadata && (
                    <>
                      <p><strong>Nodes Assessed:</strong> {reportData.deepModeReport.metadata.nodes_assessed}</p>
                      <p><strong>High-Relevance Factors:</strong> {reportData.deepModeReport.metadata.high_relevance_nodes}</p>
                      <p><strong>Execution Time:</strong> {Math.round(reportData.deepModeReport.execution_time || 0)}s</p>
                    </>
                  )}
                </div>
                {reportData.deepModePlan.hypotheses && reportData.deepModePlan.hypotheses.length > 0 && (
                  <div className="hypotheses-list">
                    <h3>Hypotheses Evaluated:</h3>
                    {reportData.deepModePlan.hypotheses.map((h: any, idx: number) => (
                      <div key={idx} className="hypothesis-item">
                        <strong>{idx + 1}. {h.title}</strong>
                        <p>{h.description}</p>
                        <span className={`status-badge status-${h.status?.toLowerCase()}`}>
                          {h.status} {'\u2022'} {h.node_count || h.nodes?.length || 0} factors
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </section>
        )}

        {/* 6. Appendix: Methodology & Evidence - grouped with Strategy Plan sections above */}
        {(reportData.strategicSynthesis || reportData.coverageAttestation) && (
          <section
            id="ar-section-appendix"
            data-bookmark-anchor
            className={`report-section appendix-section collapsible-section ${isAppendixExpanded ? 'expanded' : 'collapsed'}`}
          >
            <div
              className="section-header clickable"
              onClick={() => setIsAppendixExpanded(!isAppendixExpanded)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setIsAppendixExpanded(!isAppendixExpanded)
                }
              }}
              aria-expanded={isAppendixExpanded}
            >
              <span className="collapse-icon">{isAppendixExpanded ? '\u25BC' : '\u25B6'}</span>
              <h2>Appendix: Methodology & Evidence</h2>
            </div>
            {isAppendixExpanded && (
              <div className="section-content appendix-content">

                {/* Strategic Synthesis */}
                {reportData.strategicSynthesis && (
                  <div className="appendix-subsection">
                    <div
                      className="subsection-header clickable"
                      onClick={() => setIsSynthesisExpanded(!isSynthesisExpanded)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          setIsSynthesisExpanded(!isSynthesisExpanded)
                        }
                      }}
                      aria-expanded={isSynthesisExpanded}
                    >
                      <span className="collapse-icon">{isSynthesisExpanded ? '\u25BC' : '\u25B6'}</span>
                      <h3>Strategic Synthesis</h3>
                    </div>
                    {isSynthesisExpanded && (
                      <div className="subsection-content">
                        {reportData.strategicSynthesis.causal_narrative && (
                          <div className="synthesis-narrative">
                            <p>{reportData.strategicSynthesis.causal_narrative}</p>
                          </div>
                        )}

                        {reportData.strategicSynthesis.pillar_derivation && reportData.strategicSynthesis.pillar_derivation.length > 0 && (
                          <div className="pillar-derivation">
                            <h4>Evidence-Based Pillar Derivation</h4>
                            <div className="derivation-table-scroll">
                            <table className="derivation-table">
                              <thead>
                                <tr>
                                  <th>Pillar</th>
                                  <th>Tactics</th>
                                  <th>Confidence</th>
                                  <th>Basis</th>
                                  <th>Supporting Evidence</th>
                                  <th>KPIs</th>
                                </tr>
                              </thead>
                              <tbody>
                                {reportData.strategicSynthesis.pillar_derivation.map((pillar: any, idx: number) => (
                                  <tr key={idx} className={`confidence-${pillar.graph_confidence >= 85 ? 'high' : pillar.graph_confidence >= 70 ? 'medium' : 'low'}`}>
                                    <td className="pillar-name">
                                      <button
                                        className="nav-link pillar-link"
                                        onClick={() => handleNavigateToMece(pillar.pillar_name)}
                                        title="View in MECE Table"
                                      >
                                        {pillar.pillar_name}
                                      </button>
                                    </td>
                                    <td className="tactic-count">{pillar.tactic_count}</td>
                                    <td className="confidence-score">
                                      <span className={`confidence-badge confidence-${pillar.graph_confidence >= 85 ? 'high' : pillar.graph_confidence >= 70 ? 'medium' : 'low'}`}>
                                        {pillar.graph_confidence}%
                                      </span>
                                    </td>
                                    <td className="confidence-basis">{pillar.confidence_basis === 'graph_evidence' ? 'Graph Evidence' : 'Domain Expertise'}</td>
                                    <td className="supporting-evidence">
                                      {pillar.supporting_evidence && pillar.supporting_evidence.length > 0 ? (
                                        <ul className="evidence-list">
                                          {pillar.supporting_evidence.slice(0, 3).map((node: any, nodeIdx: number) => (
                                            <li key={nodeIdx}>
                                              <button
                                                className="nav-link node-link"
                                                onClick={() => handleNavigateToNode(node.node_id)}
                                                title="View in Business Graph"
                                              >
                                                {node.node_name}
                                              </button>
                                              <span className="node-score">({node.relevance_score?.toFixed(1) || '?'})</span>
                                            </li>
                                          ))}
                                          {pillar.supporting_evidence.length > 3 && (
                                            <li className="more-evidence">+{pillar.supporting_evidence.length - 3} more</li>
                                          )}
                                        </ul>
                                      ) : (
                                        <span className="no-evidence">Based on domain expertise</span>
                                      )}
                                    </td>
                                    <td className="associated-metrics">
                                      {pillar.associated_metrics && pillar.associated_metrics.length > 0 ? (
                                        <MetricBadgeList metrics={pillar.associated_metrics} compact={true} maxDisplay={3} />
                                      ) : (
                                        <span className="no-metrics">—</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            </div>
                          </div>
                        )}

                        {reportData.strategicSynthesis.methodology_summary && (
                          <div className="methodology-summary">
                            <h4>Analysis Methodology</h4>
                            <div className="methodology-stats">
                              <div className="stat-item">
                                <span className="stat-value">{reportData.strategicSynthesis.methodology_summary.questions_executed || 0}</span>
                                <span className="stat-label">Questions</span>
                              </div>
                              <div className="stat-item">
                                <span className="stat-value">{reportData.strategicSynthesis.methodology_summary.nodes_explored || 0}</span>
                                <span className="stat-label">Nodes Explored</span>
                              </div>
                              <div className="stat-item">
                                <span className="stat-value">{reportData.strategicSynthesis.methodology_summary.nodes_approved || 0}</span>
                                <span className="stat-label">Approved</span>
                              </div>
                              <div className="stat-item">
                                <span className="stat-value">{reportData.strategicSynthesis.methodology_summary.overall_confidence || 0}%</span>
                                <span className="stat-label">Confidence</span>
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="synthesis-navigation">
                          <span className="nav-label">Explore in detail:</span>
                          <button
                            className="btn-nav-link"
                            onClick={() => handleNavigateToMece()}
                            title="View full MECE Framework in Strategy Workspace"
                          >
                            MECE Framework
                          </button>
                          <button
                            className="btn-nav-link"
                            onClick={handleNavigateToStrategyMap}
                            title="View Strategy Map in Strategy Workspace"
                          >
                            Strategy Map
                          </button>
                          <button
                            className="btn-nav-link"
                            onClick={() => navigateToTab('graph')}
                            title="Explore Business Graph"
                          >
                            Business Graph
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Coverage Attestation */}
                {reportData.coverageAttestation && (
                  <div className="appendix-subsection">
                    <div
                      className="subsection-header clickable"
                      onClick={() => setIsCoverageExpanded(!isCoverageExpanded)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          setIsCoverageExpanded(!isCoverageExpanded)
                        }
                      }}
                      aria-expanded={isCoverageExpanded}
                    >
                      <span className="collapse-icon">{isCoverageExpanded ? '\u25BC' : '\u25B6'}</span>
                      <h3>Coverage Attestation</h3>
                    </div>
                    {isCoverageExpanded && (
                      <div className="subsection-content">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {parseImageMarkers(reportData.coverageAttestation)}
                        </ReactMarkdown>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </section>
        )}

        {/* 7. Quick Insights - rendered in default position when NOT quickInsightsFirst */}
        {!quickInsightsFirst && quickInsightsSection}

        {/* Export Actions */}
        <div className="report-actions">
          <button
            className={`export-btn ${regenStatus === 'regenerating' ? 'exporting' : ''} ${regenStatus === 'regen-done' ? 'export-success' : ''} ${regenStatus === 'regen-failed' ? 'export-error' : ''}`}
            onClick={handleRegenerateStrategyPlan}
            disabled={regenStatus === 'regenerating' || exportStatus === 'pdf-generating' || exportStatus === 'copying'}
          >
            {regenStatus === 'regenerating' ? 'Regenerating...'
              : regenStatus === 'regen-done' ? regenSuccessLabel
              : regenStatus === 'regen-failed' ? 'Regenerate Failed'
              : 'Regenerate Strategy Plan'}
          </button>
          <button
            className={`export-btn ${exportStatus === 'pdf-generating' ? 'exporting' : ''} ${exportStatus === 'pdf-done' ? 'export-success' : ''} ${exportStatus === 'pdf-failed' ? 'export-error' : ''}`}
            onClick={handleExportPdf}
            disabled={exportStatus === 'pdf-generating' || exportStatus === 'copying' || regenStatus === 'regenerating'}
          >
            {exportStatus === 'pdf-generating' ? 'Generating PDF...'
              : exportStatus === 'pdf-done' ? 'PDF Downloaded!'
              : exportStatus === 'pdf-failed' ? 'PDF Failed'
              : 'Export as PDF'}
          </button>
          <button
            className={`export-btn ${exportStatus === 'copying' ? 'exporting' : ''} ${exportStatus === 'copied' ? 'export-success' : ''} ${exportStatus === 'copy-failed' ? 'export-error' : ''}`}
            onClick={handleCopyToClipboard}
            disabled={exportStatus === 'copying' || exportStatus === 'pdf-generating' || regenStatus === 'regenerating'}
          >
            {exportStatus === 'copying' ? 'Copying...'
              : exportStatus === 'copied' ? 'Copied!'
              : exportStatus === 'copy-failed' ? 'Copy Failed'
              : 'Copy to Clipboard'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default AnalysisReportTab
